import { Box, FormHelperText, Grid, Paper, Typography } from '@mui/material';
import React, { useState } from 'react';
import { alpha, styled } from '@mui/material/styles';
import { useDispatch } from 'react-redux';

import SbButton, { SbLoadingButton } from '@Components/Button';
import DragDrop from '@Components/File/DragDrop';
import Modal from '@Components/Modal';

import Image from '@Components/Image';
import { FileResultType } from '../../../Helper/file';
import { exportFailedAWBRecords, readXslAndGetBulkAWBUploadObject } from './helper';
import { BulkAWBFailedRecord, BulkAWBType, BulkAWBUploadResponse } from './types';
import { downloadSampleAWBSheet } from './downloadSampleAWBSheet';
import API from '@Services/apiAxios';
import { SuccessErrorModalOpen } from '@Reducers/SuccessErrorModal';

interface PropsType {
  open: boolean;
  handleClose: () => void;
}

interface UploadResultType {
  total: number;
  updatedCount: number;
  failedCount: number;
  msg?: string;
}

const bulkAWBUploadAPI = (payload: { shipments: BulkAWBType[] }) => {
  return API.put<BulkAWBUploadResponse>('/shipment/admin/bulk-awb', payload);
};

function FileError({ fileError }: { fileError: string }) {
  if (!fileError.trim()) return <></>;

  return (
    <FormHelperText error className="mt-1">
      {fileError}
    </FormHelperText>
  );
}

function BulkAWBUpload(props: PropsType) {
  const [xlsFileData, setXlsFileData] = useState<BulkAWBType[] | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [fileError, setFileError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [failedRecords, setFailedRecords] = useState<BulkAWBFailedRecord[] | null>(null);
  const [uploadResult, setUploadResult] = useState<UploadResultType | null>(null);
  const dispatch = useDispatch();

  const onDone = async () => {
    if (loading || !xlsFileData) return;

    if (xlsFileData.length > 500) {
      setFileError(
        'You can upload a maximum of 500 shipments at a time. Please reduce the number of shipments and try again.',
      );
      return;
    }

    try {
      setLoading(true);
      setApiError('');
      setFailedRecords(null);
      setUploadResult(null);

      const payload = {
        shipments: xlsFileData,
      };

      const res = await bulkAWBUploadAPI(payload);
      const data = res?.data;

      const total = data?.total ?? 0;
      const updatedCount = data?.updatedCount ?? 0;
      const failedCount = data?.failedCount ?? 0;
      const failedList = data?.failed || [];

      const responseMsg = data?.msg || `Processed ${total} entries: ${updatedCount} updated, ${failedCount} failed.`;

      if (failedCount > 0 && failedList.length > 0) {
        // Keep modal OPEN and show failed records download option inside modal
        setFailedRecords(failedList);
        setUploadResult({
          total,
          updatedCount,
          failedCount,
          msg: responseMsg,
        });
      } else {
        dispatch(
          SuccessErrorModalOpen({
            type: 'success',
            title: 'Success',
            subTitle: responseMsg,
          }),
        );
        onClose();
      }
    } catch (error: any) {
      const msg =
        error?.response?.data?.msg || error?.response?.data?.message || error?.message || 'Bulk AWB upload failed';

      setApiError(msg);
      dispatch(
        SuccessErrorModalOpen({
          type: 'error',
          title: 'Oops!',
          subTitle: msg,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const onFileChange = (data: FileResultType[]) => {
    try {
      setFileError('');
      setApiError('');
      setFileName('');
      setFailedRecords(null);
      setUploadResult(null);

      if (data?.[0]?.fileName) {
        setFileName(data[0].fileName);
      }

      const bulkAWBData = readXslAndGetBulkAWBUploadObject(data);
      setXlsFileData(bulkAWBData);
    } catch (error: any) {
      if (error?.msg) {
        setFileError(error.msg);
        setXlsFileData(null);
      }
    }
  };

  const onDownloadSampleSheet = () => {
    downloadSampleAWBSheet();
  };

  const onClose = () => {
    props.handleClose();
    setFileError('');
    setFileName('');
    setXlsFileData(null);
    setApiError('');
    setLoading(false);
    setFailedRecords(null);
    setUploadResult(null);
  };

  return (
    <Modal open={props.open} sx={{ minHeight: '470px', bottom: 0 }} maxWidth="sm">
      <Grid container>
        <Grid container item mb={1}>
          <Typography fontWeight="bold" variant="h6">
            Bulk AWB Upload
          </Typography>
        </Grid>

        <Grid container item>
          <DragDrop
            readType="xls"
            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            subText={fileName ? fileName : 'xlsx or xls file size no more than 10MB'}
            onChange={onFileChange}
          />

          <FileError fileError={fileError || apiError} />
        </Grid>

        {uploadResult && failedRecords && failedRecords.length > 0 && (
          <Box
            sx={{
              width: '100%',
              mt: 2.5,
              p: 2.5,
              borderRadius: 2,
              bgcolor: theme => alpha(theme.palette.error.main, 0.03),
              border: theme => `1px solid ${alpha(theme.palette.error.main, 0.15)}`,
            }}
          >
            <Typography variant="subtitle1" fontWeight="700" textAlign="center" color="text.primary">
              Processing Complete
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" mb={2}>
              Bulk AWB upload processing results
            </Typography>

            <Grid container spacing={1.5} justifyContent="center" mb={2.5}>
              <Grid item xs={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    textAlign: 'center',
                    borderRadius: 2,
                    border: '1px solid #EAECF0',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" display="block" fontWeight="600">
                    Total
                  </Typography>
                  <Typography variant="h6" fontWeight="700" color="text.primary">
                    {uploadResult.total}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    textAlign: 'center',
                    borderRadius: 2,
                    border: theme => `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                    bgcolor: theme => alpha(theme.palette.success.main, 0.04),
                  }}
                >
                  <Typography variant="caption" color="success.main" display="block" fontWeight="600">
                    Updated
                  </Typography>
                  <Typography variant="h6" fontWeight="700" color="success.main">
                    {uploadResult.updatedCount}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    textAlign: 'center',
                    borderRadius: 2,
                    border: theme => `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                    bgcolor: theme => alpha(theme.palette.error.main, 0.04),
                  }}
                >
                  <Typography variant="caption" color="error.main" display="block" fontWeight="600">
                    Failed
                  </Typography>
                  <Typography variant="h6" fontWeight="700" color="error.main">
                    {uploadResult.failedCount}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: theme => `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                bgcolor: theme => alpha(theme.palette.warning.main, 0.04),
                textAlign: 'center',
              }}
            >
              <Typography variant="subtitle2" fontWeight="700" color="warning.dark" mb={0.5}>
                Attention Required
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                {uploadResult.failedCount === 1
                  ? '1 record could not be updated.'
                  : `${uploadResult.failedCount} records could not be updated.`}{' '}
                Download the failed records to review and correct them.
              </Typography>

              <SbButton
                variant="contained"
                color="secondary"
                fullWidth
                onClick={() => exportFailedAWBRecords(failedRecords)}
                startIcon={
                  <Image src="/images/upload_document.svg" alt="Download Failed Records" width={25} height={25} />
                }
                sx={{
                  py: 1,
                  fontWeight: 600,
                  borderRadius: '8px !important',
                  textTransform: 'none',
                }}
              >
                Download Failed Records
              </SbButton>
            </Paper>
          </Box>
        )}

        <Grid container item mt={2} py={failedRecords?.length ? 2 : 4} justifyContent="center">
          <Grid item xs={12} md="auto">
            <DownloadSampleSheet
              fullWidth
              variant="outlined"
              onClick={onDownloadSampleSheet}
              startIcon={
                <Image
                  src="/images/upload_document.svg"
                  alt="Speedbox"
                  width={35}
                  height={35}
                  style={{ transform: 'rotate(180deg)' }}
                />
              }
            >
              Download sample file
            </DownloadSampleSheet>

            <Typography variant="subtitle2" className="sb-text-gray text-center">
              xlsx or xls file size no more than 10MB
            </Typography>
          </Grid>
        </Grid>

        <Grid container item spacing={2} justifyContent="center">
          <Grid item xs={12} sm={6} md={4}>
            <SbButton variant="outlined" fullWidth onClick={onClose}>
              Cancel
            </SbButton>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <SbLoadingButton
              variant="contained"
              fullWidth
              disabled={!xlsFileData || loading}
              onClick={onDone}
              loading={loading}
            >
              Done
            </SbLoadingButton>
          </Grid>
        </Grid>
      </Grid>
    </Modal>
  );
}

export default BulkAWBUpload;

const DownloadSampleSheet = styled(SbButton)(({ theme }) => ({
  border: `1px solid #EAECF0`,
  borderRadius: '8px !important',
  backgroundColor: alpha(theme.palette.primary.main, 0.03),
  '&:hover': {
    border: `1px solid #EAECF0`,
  },
}));
